import React, { useState, useEffect, useCallback } from 'react';
import { Layers, Package, RefreshCw, TrendingUp, TrendingDown, Boxes, KeyRound, ShieldAlert } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { getInventarioPorLote, verificarSuperCodigo } from '../../lib/api';
import { EGG_LABELS } from './constants';
import { toast } from 'sonner';

const ORDEN_TIPOS = ['grande', 'mediano', 'pequeño', 'doble', 'sucio', 'casquiado'];
const fmt = (n) => Number(n || 0).toLocaleString('en-US');

/**
 * Muestra el inventario disponible desagregado por lote (FIFO):
 *  - Acceso gated por código de Super Usuario (mismo patrón que Traslados).
 *  - Un card por tipo de huevo
 *  - Cada card lista los lotes activos con su stock disponible
 *  - Se marca el lote más viejo como "próximo a despachar" (FIFO)
 *  - Total global también visible
 */
export const InventarioPorLoteTab = () => {
  // Gate de acceso por Super Usuario (misma UX que Traslados)
  const [unlocked, setUnlocked] = useState(false);
  const [codigoInput, setCodigoInput] = useState('');
  const [verifyingCode, setVerifyingCode] = useState(false);

  const [porTipo, setPorTipo] = useState({});
  const [loading, setLoading] = useState(true);
  const [ultimaActualizacion, setUltimaActualizacion] = useState(null);

  const cargarData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getInventarioPorLote();
      setPorTipo(res.data.por_tipo || {});
      setUltimaActualizacion(new Date());
    } catch (err) {
      toast.error('No se pudo cargar el inventario por lote');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (unlocked) cargarData();
  }, [unlocked, cargarData]);

  const handleUnlock = async () => {
    const c = codigoInput.trim();
    if (c.length < 4) { toast.error('Ingrese el código de Super Usuario'); return; }
    setVerifyingCode(true);
    try {
      await verificarSuperCodigo(c);
      setUnlocked(true);
      setCodigoInput('');
      toast.success('Acceso otorgado');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Código incorrecto');
    } finally {
      setVerifyingCode(false);
    }
  };

  const tiposConDatos = ORDEN_TIPOS.filter(
    (t) => (porTipo[t] || porTipo[t === 'pequeño' ? 'pequeno' : t] || []).some((l) => l.disponible > 0 || l.producido > 0),
  );

  // ============== VISTA CON GATE ==============
  if (!unlocked) {
    return (
      <Card data-testid="por-lote-locked">
        <CardHeader>
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Layers className="w-5 h-5 text-primary" />
            Inventario por Lote (FIFO)
          </CardTitle>
        </CardHeader>
        <CardContent className="max-w-md mx-auto py-8 space-y-4">
          <div className="flex items-start gap-2 text-xs bg-amber-50 dark:bg-amber-950/30 border border-amber-200 rounded-md p-2.5">
            <ShieldAlert className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
            <p className="text-amber-900 dark:text-amber-200">
              Esta pestaña muestra el <strong>desglose de inventario por lote</strong> (FIFO).
              Solo Super Usuario puede visualizarla. Ingrese el código para continuar.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="por-lote-code-input" className="flex items-center gap-1.5">
              <KeyRound className="w-4 h-4" />
              Código de Super Usuario
            </Label>
            <Input
              id="por-lote-code-input"
              type="password"
              value={codigoInput}
              onChange={(e) => setCodigoInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !verifyingCode) handleUnlock(); }}
              placeholder="Ingrese el código"
              className="h-11 font-mono"
              autoFocus
              data-testid="por-lote-code-input"
            />
          </div>
          <Button
            onClick={handleUnlock}
            disabled={verifyingCode || codigoInput.trim().length < 4}
            className="w-full h-11"
            data-testid="por-lote-unlock-btn"
          >
            {verifyingCode ? 'Verificando…' : 'Desbloquear acceso'}
          </Button>
        </CardContent>
      </Card>
    );
  }

  // ============== VISTA DESBLOQUEADA ==============
  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
              <Layers className="w-5 h-5" /> Inventario por Lote (FIFO)
            </CardTitle>
            <CardDescription className="mt-1">
              Distribución del stock actual por lote (FIFO). El total cuadra con el inventario global — los lotes viejos ya se consumieron, por eso lo que queda proviene mayormente del lote más nuevo.
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={cargarData}
            disabled={loading}
            data-testid="btn-refresh-inv-lote"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        {ultimaActualizacion && (
          <p className="text-[11px] text-muted-foreground">
            Actualizado: {ultimaActualizacion.toLocaleTimeString('es-ES')}
          </p>
        )}
      </CardHeader>
      <CardContent>
        {loading && Object.keys(porTipo).length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">Cargando…</div>
        ) : tiposConDatos.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground">
            <Boxes className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>Aún no hay producción registrada por lote.</p>
            <p className="text-xs mt-1">Cuando registres producción, verás aquí el desglose FIFO.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {tiposConDatos.map((tipo) => {
              const lotes = porTipo[tipo] || porTipo[tipo === 'pequeño' ? 'pequeno' : tipo] || [];
              const totalDisponible = lotes.reduce((acc, l) => acc + Number(l.disponible || 0), 0);
              const totalProducido = lotes.reduce((acc, l) => acc + Number(l.producido || 0), 0);
              const totalVendido = lotes.reduce((acc, l) => acc + Number(l.vendido || 0), 0);
              return (
                <div
                  key={tipo}
                  className="border border-border rounded-lg overflow-hidden bg-card"
                  data-testid={`card-inv-lote-${tipo}`}
                >
                  <div className="bg-muted/40 px-4 py-2.5 border-b border-border">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <Package className="w-4 h-4 text-primary" />
                        <span className="font-semibold capitalize text-sm" style={{ fontFamily: 'Space Grotesk' }}>
                          {EGG_LABELS[tipo] || tipo}
                        </span>
                      </div>
                      <Badge className="tabular-nums" data-testid={`total-disponible-${tipo}`}>
                        {fmt(totalDisponible)}
                      </Badge>
                    </div>
                    <div className="mt-1 flex items-center gap-3 text-[10px] text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <TrendingUp className="w-3 h-3 text-emerald-500" />
                        Prod: {fmt(totalProducido)}
                      </span>
                      <span className="flex items-center gap-1">
                        <TrendingDown className="w-3 h-3 text-red-500" />
                        Vend: {fmt(totalVendido)}
                      </span>
                    </div>
                  </div>
                  <div className="divide-y divide-border/60">
                    {lotes.length === 0 && (
                      <div className="px-4 py-3 text-xs text-muted-foreground">Sin lotes con datos</div>
                    )}
                    {lotes.map((lote, idx) => (
                      <div
                        key={lote.lote_id}
                        className={`px-4 py-2 flex items-center justify-between gap-2 ${
                          idx === 0 && lote.disponible > 0 ? 'bg-primary/5' : ''
                        }`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <div>
                            <div className="text-xs font-medium flex items-center gap-1">
                              {/^lote\b/i.test(String(lote.lote_codigo || '')) ? lote.lote_codigo : `Lote ${lote.lote_codigo || '—'}`}
                              {idx === 0 && lote.disponible > 0 && (
                                <Badge
                                  variant="default"
                                  className="text-[9px] px-1.5 py-0 h-4 gap-0.5 whitespace-nowrap"
                                >
                                  FIFO
                                </Badge>
                              )}
                            </div>
                            {lote.fecha_inicio && (
                              <div className="text-[10px] text-muted-foreground">
                                desde {lote.fecha_inicio}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <div
                            className={`text-sm tabular-nums font-semibold ${
                              lote.disponible > 0
                                ? 'text-emerald-700 dark:text-emerald-400'
                                : 'text-muted-foreground'
                            }`}
                            data-testid={`disponible-${tipo}-${lote.lote_id}`}
                          >
                            {fmt(lote.disponible)}
                          </div>
                          <div className="text-[9px] text-muted-foreground tabular-nums">
                            {fmt(lote.producido)} prod · {fmt(lote.vendido)} vend
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default InventarioPorLoteTab;
